import React, {useContext, useMemo} from "react";
import {T, useTranslate} from "@tolgee/react";
import {CurrencyContext} from "view/context/CurrencyContext";
import Paper from "@material-ui/core/Paper";
import {FullProduct} from "ts/api/product";
import {uniqBy} from "lodash";
import Divider from "@material-ui/core/Divider";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import formatPrice from "tools/formats/formatPrice";

import {PaymentMethodType} from "ts/api/paymentMethod";
import groupBy from "lodash.groupby";
import {NON_APPROVAL_ORDER_GROUP} from "view/components/ShoppingSummary/context";
import {MIN_AMOUNT_BY_CURRENCY} from "view/apps/Exhibitor/pages/Cart/components/Payment/shared";
import useBranding from "store/branding/branding.store";
import {BrandingType, IShow} from "@evennt-tech/api-v2-core";
import {Products, SummaryItem} from "./Products";
import useStyles from "./styles";

export type ShoppingSummaryProps = {
  show: IShow;
  subtotalAmount: number;
  totalAmount: number;
  taxAmount: number;
  paymentFeeAmount: number;
  discountAmount: number;
  paymentMethodType: PaymentMethodType;
  productsWithApproval: FullProduct[];
  productsWithoutApproval: FullProduct[];
};

const ShoppingSummary: React.FC<ShoppingSummaryProps> = ({
  subtotalAmount,
  totalAmount,
  taxAmount,
  paymentFeeAmount,
  discountAmount,
  show,
  paymentMethodType,
  productsWithoutApproval,
  productsWithApproval,
}) => {
  const classes = useStyles();
  const {t} = useTranslate();
  const currency = useContext(CurrencyContext);
  const {salesTaxName, salesTaxPercentage} = show;
  const MIN_PAYMENT_AMOUNT = MIN_AMOUNT_BY_CURRENCY[currency];

  const taxTitle = `${salesTaxName} (${salesTaxPercentage}%)`;

  const fees = useMemo(() => {
    const allFees = productsWithoutApproval
      .map((product) => product?.fees)
      .flat(1);
    return uniqBy(allFees, "id");
  }, [productsWithoutApproval]);

  const productsWithApprovalGroupedByCompany = useMemo(
    () => groupBy(productsWithApproval, "owner.id"),
    [productsWithApproval]
  );

  const totalValue = discountAmount
    ? Math.max(MIN_PAYMENT_AMOUNT, totalAmount)
    : totalAmount;

  const {brandings} = useBranding();
  const branding = brandings[show.id];

  return (
    <Paper elevation={0} variant="outlined" className={classes.paper}>
      <Typography variant="h3">
        <T keyName="common.shopping_summary.title" />
      </Typography>
      <Box
        marginTop={3}
        maxHeight={
          branding?.backgroundType === BrandingType.IMAGE ? "380px" : "unset"
        }
        style={{overflowY: "auto"}}
      >
        {Object.entries(productsWithApprovalGroupedByCompany).map(
          ([ownerId, products], index) => {
            return (
              !!products.length && (
                <Products
                  key={index}
                  isApprovalRequired
                  products={products}
                  ownerId={ownerId}
                />
              )
            );
          }
        )}
        {!!productsWithoutApproval.length && (
          <Products
            products={productsWithoutApproval}
            ownerId={NON_APPROVAL_ORDER_GROUP}
          />
        )}
        {!!fees?.length && (
          <>
            {fees.map((fee) => (
              <Box key={fee.name} mr={1}>
                <SummaryItem
                  title={fee.name}
                  value={formatPrice(fee?.amountWithFees, currency)}
                />
              </Box>
            ))}
            <Divider />
          </>
        )}
      </Box>
      <Box
        marginBottom={3}
        marginTop={fees?.length ? 3 : 0}
        className={classes.list}
      >
        {!!paymentFeeAmount &&
          paymentMethodType === PaymentMethodType.bankTransfer && (
            <SummaryItem
              title={t("common.shopping_summary.payment_fee")}
              value={formatPrice(paymentFeeAmount, currency)}
            />
          )}
        {}
        {!!discountAmount && (
          <Box mb={3} mt={-0.5}>
            <SummaryItem
              title={t("common.shopping_summary.discount")}
              value={formatPrice(discountAmount, currency)}
              highlight
            />
            <Divider />
          </Box>
        )}
        <SummaryItem
          title={t("common.shopping_summary.sub_total")}
          value={formatPrice(subtotalAmount, currency)}
        />
        <SummaryItem
          title={taxTitle}
          value={formatPrice(taxAmount, currency)}
        />
      </Box>
      <Divider />
      <Box marginTop={3} className={classes.list}>
        <SummaryItem
          bold
          title={t("common.shopping_summary.total")}
          value={formatPrice(totalValue, currency)}
        />
      </Box>
    </Paper>
  );
};

export default ShoppingSummary;
